import {
    attach,
    createEffect,
    createEvent,
    createStore,
    type EffectParams,
    type StoreValue,
    sample,
    split,
} from 'effector'
import { or } from 'patronum'
import { z } from 'zod'

import { getRegisterInfo } from '@portal/client-info'
import { logger } from '@portal/logger'

import { portalProfileAPI } from '@/entities/profile/portal'
import { createPasswordValidation } from '@/features/password-requirement/factory'
import { getPortalAPIErrorMessage } from '@/shared/api/portal/error-message'
import { $i18n, $lang, attachI18n, type Language } from '@/shared/lib/i18n'
import { navigateTo } from '@/shared/lib/next'
import { toast } from '@/shared/lib/toast'
import { modal } from '@/shared/ui/modal'

import { signupFormDataSchema } from './schema'
import * as steps from './steps'

export const fieldUpdated = createEvent<{ key: string; value: string }>()

const resetForm = createEvent()

export const $form = createStore<{ [key: string]: string }>({})
    .on(fieldUpdated, (form, { key, value }) => ({
        ...form,
        [key]: value,
    }))
    .reset(resetForm)

export const $email = $form.map(({ email }) => email ?? '')
const $password = $form.map(({ password }) => password?.trim() ?? '')

export const {
    $isPasswordShort,
    $isPasswordWeak,
    $passwordLongEnough,
    $passwordHasLowerAndUppercase,
    $passwordHasDigitOrSpecialCharacter,
} = createPasswordValidation({ $password })

export const signupFormSubmitted = createEvent<{ skipValidation: boolean }>()

const signupFx = attach({ effect: portalProfileAPI.emailSignup })

const checkSignupFormFx = createEffect<
    {
        formData: StoreValue<typeof $form>
        lang: Language
        skipValidation: boolean
    },
    EffectParams<typeof signupFx>
>(({ formData, lang, skipValidation }) => {
    const { email, password, userAgreeTerms } = signupFormDataSchema.parse(formData)

    return {
        email,
        password,
        register_info: getRegisterInfo(lang),
        skip_email_validation: skipValidation,
        user_agree_terms: userAgreeTerms,
    }
})

sample({
    clock: signupFormSubmitted,
    source: { lang: $lang, formData: $form },
    fn: ({ lang, formData }, { skipValidation }) => ({ formData, lang, skipValidation }),
    target: checkSignupFormFx,
})

sample({
    clock: checkSignupFormFx.doneData,
    target: signupFx,
})

sample({
    clock: checkSignupFormFx.failData,
    target: [
        attachI18n(toast.errorFx, 'TEXT_TOAST_FAIL'),
        createEffect((error: Error) => logger.error('signup form validation failed', error)),
    ],
})

sample({
    clock: signupFx.done,
    // NOTE: close all modals, because auth dialog is using this form
    target: [modal.closeAll, navigateTo('/play', { replace: true }), resetForm],
})

const signupFailedStatus = split(signupFx.fail, {
    emailMaybeUndeliverable: ({ error }) => error.code === 'email_maybe_undeliverable',
    accountAlreadyExist: ({ error }) => error.code === 'account_already_exist',
    passwordTooSimple: ({ error }) => error.code === 'password_too_simple',
})

export const $undeliverableEmail = createStore<{ email: string }>({ email: '' })

sample({
    clock: signupFailedStatus.emailMaybeUndeliverable,
    fn: ({ params }) => ({
        email: params.email,
    }),
    target: [steps.showStepEmailMaybeUndeliverable, $undeliverableEmail],
})

sample({
    clock: signupFailedStatus.accountAlreadyExist,
    target: steps.showStepAccountAlreadyExists,
})

sample({
    clock: signupFailedStatus.passwordTooSimple,
    target: steps.showStepPasswordTooSimple,
})

const $signupError = createStore<{ message: string }>({ message: '' })
export const $signupErrorMessage = $signupError.map(({ message }) => message)

sample({
    clock: signupFailedStatus.__,
    source: $i18n,
    fn: ({ i18n }, { error }) => ({ message: getPortalAPIErrorMessage(error, i18n) }),
    target: [steps.showStepSignupFailed, $signupError],
})

// ------------ Email LOGIN

export const loginFormSubmitted = createEvent()

const $loginError = createStore<{ message: string }>({ message: '' }).reset(loginFormSubmitted)
export const $loginErrorMessage = $loginError.map(({ message }) => message)

const loginFx = attach({ effect: portalProfileAPI.emailLogin })

// TODO offer registration if account not found ????????????????????
sample({
    clock: loginFx.fail,
    source: $i18n,
    fn: ({ i18n }, { error }) => ({ message: getPortalAPIErrorMessage(error, i18n) }),
    target: $loginError,
})

sample({
    clock: loginFx.done,
    // NOTE: close all modals, because auth dialog is using this form
    target: [modal.closeAll, navigateTo('/play', { replace: true }), resetForm],
})

const loginFormDataSchema = z.object({
    email: z.email(),
    password: z.string(),
})

const checkLoginFormFx = createEffect<
    {
        formData: StoreValue<typeof $form>
    },
    EffectParams<typeof loginFx>
>(({ formData }) => {
    const { email, password } = loginFormDataSchema.parse(formData)

    return {
        email,
        password,
    }
})

sample({
    clock: loginFormSubmitted,
    source: { formData: $form },
    target: checkLoginFormFx,
})

sample({
    clock: checkLoginFormFx.doneData,
    target: loginFx,
})

export const $loginValidationError = createStore('').reset($email)

sample({
    clock: checkLoginFormFx.failData,
    source: $i18n,
    fn: ({ i18n }) => i18n.EMAIL_CHANGE_ERROR__EMAIL_FORMAT,
    target: $loginValidationError,
})

sample({
    clock: checkLoginFormFx.failData,
    target: createEffect((error: Error) => logger.error('login form validation failed', error)),
})

// ---------

export const $interactionDisabled = or(loginFx.pending, signupFx.pending)
